import { Module, VuexModule, Mutation, Action, getModule } from 'vuex-module-decorators'
import { store } from '../../store'
import { User } from '../../models/user'
import { Organization } from '../../models/organization'
import { authStore } from '../../store/modules/auth.store'
import { usersApi } from '../../services/api/users.api'
import { orgApi } from '../../services/api/organization.api'
import { LocalStorage } from '../../services/local-storage'
import userflow from 'userflow.js'
import { appConfig } from '../../services/app-config'

@Module({
    dynamic: true,
    namespaced: true,
    name: 'user',
    store,
})
class UserStore extends VuexModule {
    private stateModel: User | null = null

    get user() {
        return this.stateModel
    }

    get isAdmin() {
        if (this.user) {
            return this.user!.role.name === 'Admin'
        } else {
            return false
        }
    }

    get isOrgAdmin() {
        if (this.user) {
            return this.user!.role.name === 'Org Admin'
        } else {
            return false
        }
    }

    get isAdminOrOrgAdmin() {
        return this.isAdmin || this.isOrgAdmin
    }

    get canAddOrganization() {
        return this.isAdmin || this.isOrgAdmin
    }

    get canDeleteOrganization() {
        return this.isAdmin || this.isOrgAdmin
    }

    get canViewOrgUsers() {
        return this.isAdmin || this.isOrgAdmin
    }

    get canInviteUser() {
        return this.isOrgAdmin
    }

    get currentOrg() {
        return this.user?.currentOrganization
    }

    get currentOrgId() {
        return this.user ? this.user.currentOrganization.id : -1
    }

    get avatar() {
        return this.stateModel?.avatar
    }

    @Mutation
    setUser(user: User | null) {
        this.stateModel = user
    }

    @Mutation
    setAvatar(avatar: string) {
        this.stateModel!.avatar = avatar
    }
    
    @Action({ rawError: true })
    async init() {
        await this.getUserInfo()

        const paq = window['_paq']
        if (paq) {
            paq.push(['setUserId', this.user?.email])
            paq.push(['trackPageView']);
            paq.push(['enableLinkTracking']);
        }
        userflow.init(appConfig.userflowToken!)
        userflow.identify(this.user!.id.toString(), {
            name: this.user!.firstName + ' ' + this.user!.lastName,
            email: this.user!.email,
            signed_up_at: this.user!.created
                ? new Date(this.user!.created).toISOString()
                : new Date(Date.now()).toISOString(),
            role: this.user!.role.name,
            org_id: this.user!.currentOrganization.id.toString(),
            org_name: this.user!.currentOrganization.name,
            guide_url: `https://guides.hexiq.com/${this.user!.currentOrganization.id}/getting-started.html`,
        })
    }

    @Action({ rawError: true })
    async getUserInfo(data: { userId: number, orgId: number } | null = null) {
        if (!data) {
            if (this.user) {
                data = {
                    // use current user info if data is null and user exists
                    userId: this.user.id,
                    orgId: this.user.currentOrganization.id
                }
            } else {
                data = {
                    // use info from token if user is null
                    userId: authStore.decodedToken!.nameid,
                    orgId: authStore.decodedToken!.orgId
                }
            }
        }
        const user = await usersApi.getUser(data.userId, data.orgId)
        this.setUser(user)
        this.getAvatar()
        return user
    }

    @Action({ rawError: true })
    getOrganizationUsers() {
        return usersApi
            .getUsersByOrgId(this.currentOrgId)
            .then(users => {
                return users
            })
            .catch(error => {
                console.log(error)
                return []
            })
    }

    @Action({ rawError: true })
    createUserOrganization(data: { userId: number, org: Organization, loginAfterCreated: boolean }) {
        return orgApi
            .createOrganization(data.userId, data.org.name)
            .then(async res => {
                const org: Organization = { ...data.org }
                org.id = res.orgId

                if (data.loginAfterCreated) {
                    authStore.logout()
                    await authStore.setToken({ token: res.token as string, rememberMe: LocalStorage.isTokenRemembered() })
                    await this.getUserInfo({ userId: data.userId, orgId: org.id })
                }
                return org
            })
    }

    @Action({ rawError: true })
    renameOrganization(data: { userId: number, org: Organization }) {
        return orgApi
            .renameOrganization(data.userId, data.org)
            .then(async () => {
                await this.getUserInfo()
            })
    }

    @Action({ rawError: true })
    removeOrganization(orgId: number) {
        return orgApi.removeOrganization(orgId)
            .then(async () => {
                await this.getUserInfo()
            })
    }

    @Action({ rawError: true })
    async getAvatar() {
        this.setAvatar(await usersApi.getAvatar(this.user!.id))
    }
}

export const userStore = getModule(UserStore)
